/**
 * Telemetry flags — owners' corrections to their Live visits rows (the accuracy loop, 27 October
 * 2026; lambdas/reporting/src/visit-flags.ts). Each zone keeps the flags on its own visits, so the
 * list fans out to every zone and merges into one zone-agnostic list with a per-zone legend.
 * Correct / Keep go to the zone the flag lives in.
 */
import { adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, zoneByCode, HOME_ZONE, type PortalZone } from './zones';

export type FlagReason = 'wrong_source' | 'me_or_team' | 'bot' | 'other';
export type FlagStatus = 'open' | 'corrected' | 'kept';
export type FlagSource = 'direct' | 'google' | 'facebook' | 'instagram' | 'linkedin' | 'other_referrer'
  | 'tiktok' | 'youtube' | 'pinterest' | 'email' | 'qr' | 'messaging' | 'paid' | 'other';

export const FLAG_SOURCE_LABEL: Record<FlagSource, string> = {
  direct: 'Direct', google: 'Google search', facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn',
  other_referrer: 'Other referrer', tiktok: 'TikTok', youtube: 'YouTube', pinterest: 'Pinterest', email: 'Email',
  qr: 'QR code', messaging: 'Messaging app', paid: 'Paid ad', other: 'Other',
};
export const FLAG_REASON_LABEL: Record<FlagReason, string> = {
  wrong_source: 'The source is wrong', me_or_team: 'This was me / my team', bot: 'This is a bot', other: 'Something else',
};
/** The sources ops can apply (an owner's "Other" is resolved to one of these, or kept). */
export const APPLICABLE_SOURCES = (Object.keys(FLAG_SOURCE_LABEL) as FlagSource[]).filter((s) => s !== 'other');

export type AppliedCorrection =
  | { kind: 'source'; source: Exclude<FlagSource, 'other'>; liveSource: string; channel: string; origin: string }
  | { kind: 'exclude'; as: 'team' | 'bot' };

export interface FlaggedRow {
  startedAt: string; source: string | null; why: string; agent: string; landing: string; device: string; browser: string;
  region: string; country: string; pageCount: number; clickCount: number; durationSeconds: number;
}

export interface TelemetryFlagRow {
  flagId: string; visitId: string; domainToken: string; accountId: string; customer: string; hostname: string;
  createdAt: string; ownerTimeZone: string; reason: FlagReason; suggestedSource?: FlagSource; text: string;
  row: FlaggedRow | null;
  /** What the snippet saw (the server's evidence, minus the tab tokens). */
  evidence: Record<string, unknown> | null;
  status: FlagStatus; reviewer: string; reviewedAt: string; reply: string; applied?: AppliedCorrection;
  /** Added by the client after the fan-out. */
  zone?: string;
}

export interface TelemetryFlagsResult { rows: TelemetryFlagRow[]; failedZones: string[] }

export async function listTelemetryFlags(): Promise<TelemetryFlagsResult> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const r = await adminApiInZone<{ rows: TelemetryFlagRow[] }>(zone, '/admin/telemetry-flags');
    return (r.rows ?? []).map((row) => ({ ...row, zone: zone.code }));
  });
  throwIfAllZonesFailed(results);
  return {
    rows: results.flatMap((r) => r.items).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    failedZones: failedZoneLabels(results),
  };
}

function zoneOf(row: TelemetryFlagRow): PortalZone {
  return zoneByCode(row.zone) ?? HOME_ZONE;
}
const flagPath = (row: TelemetryFlagRow, verb: 'correct' | 'keep') =>
  `/admin/telemetry-flags/${encodeURIComponent(row.domainToken)}/${encodeURIComponent(row.visitId)}/${verb}`;

export type CorrectionInput = { kind: 'source'; source: Exclude<FlagSource, 'other'>; note?: string } | { kind: 'exclude'; as: 'team' | 'bot'; note?: string };

export async function correctTelemetryFlag(row: TelemetryFlagRow, input: CorrectionInput): Promise<void> {
  await adminApiInZone(zoneOf(row), flagPath(row, 'correct'), { method: 'POST', body: JSON.stringify(input) });
}

export async function keepTelemetryFlag(row: TelemetryFlagRow, reply: string): Promise<void> {
  await adminApiInZone(zoneOf(row), flagPath(row, 'keep'), { method: 'POST', body: JSON.stringify({ reply }) });
}
